import React, { useState, useCallback, useEffect } from "react";
import { generateHash, getIdentifier } from "../../lib/util";
import { useAccessToken } from "../../lib/services/auth-service";
import { usePublicFreeTicketRegister } from "../../lib/services/event-service";
import { internationalCodes } from "../../assets";

const RegisterForm = ({
  onBack,
  onSuccess,
  onPayment,
  event,
  tickets,
  paidTickets,
  paymentInfo,
}) => {
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCode, setSelectedCode] = useState("+1");
  const [error, setError] = useState("");
  const [registeredUser, setRegisteredUser] = useState("");
  const [hash, setHash] = useState(null);
  const [loading, setLoading] = useState(false);

  const identifier = getIdentifier();
  useEffect(() => {
    const getHash = async () => {
      const hash = await generateHash({
        identifier,
        tokenType: "system",
      });
      setHash(hash);
    };
    if (paymentInfo.amount <= 0) {
      setLoading(false);
    }
    if (!hash) {
      getHash();
    }
  }, [paymentInfo]); // Empty dependency array means this runs once on mount

  const { isLoading: isLoading, mutate: mutateRegister } =
    usePublicFreeTicketRegister({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        const { event, ticket } = response.data;
        onSuccess({
          event,
          ticket,
          user: registeredUser,
        });
      },
    });

  const { isLoading: isTokenLoading, mutate: mutateToken } = useAccessToken({
    onError: (response) => {
      setError(response.message);
    },
    onSuccess: async (response) => {
      const { token } = response.data;
      setLoading(true);
      onPayment({
        tickets,
        user: registeredUser,
        token,
      });
    },
  });

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
    setError("");
  }, []);

  const handlePhoneNumberChange = useCallback((e) => {
    setPhoneNumber(e.target.value);
    setError("");
  }, []);

  const handleCodeChange = useCallback((e) => {
    setSelectedCode(e.target.value);
    setError("");
  }, []);

  const handleRegisterSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());
      const normalizedPhoneNumber =
        phoneNumber.length >= 11 && phoneNumber.startsWith("0")
          ? phoneNumber.slice(1)
          : phoneNumber;
      const user = {
        ...data,
        intCode: selectedCode,
        phone: normalizedPhoneNumber,
        email: email,
      };
      setRegisteredUser(user);
      if (!paidTickets) {
        mutateRegister({
          ticketItems: tickets,
          user,
          eventId: event.eventId,
          channel: "WEB",
        });
      } else {
        mutateToken({
          identifier,
          accessLevel: "regular",
          tokenType: "system",
          hash,
        });
      }
    },
    [email, phoneNumber, selectedCode, mutateRegister],
  );

  return (
    <>
      <button className="back-button" onClick={onBack}>
        <i className="fa fa-circle-arrow-left" aria-hidden="true"></i>
      </button>
      <div className="auth-form-container">
        <>
          <h2>Register for Event</h2>
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleRegisterSubmit}>
            <div className="input-box">
              <label htmlFor="firstName">First Name:</label>
              <div className="input-text">
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  placeholder="First Name"
                />
              </div>
            </div>
            <div className="input-box">
              <label htmlFor="lastName">Last Name:</label>
              <div className="input-text">
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  placeholder="Last Name"
                />
              </div>
            </div>

            <div className="input-box">
              <label htmlFor="email">Email:</label>
              <div className="input-text">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  placeholder="Email"
                />
              </div>
            </div>
            <div className="input-box">
              <label htmlFor="phone">Phone Number:</label>
              <div className="input-text">
                <select
                  id="code"
                  name="code"
                  value={selectedCode}
                  onChange={handleCodeChange}
                  required
                >
                  {internationalCodes.map((code) => (
                    <option key={code.code} value={code.code}>
                      {code.code} ({code.countryCode})
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  required
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            <div className="login-container">
              <button
                type="submit"
                className="auth-submit-button"
                disabled={isLoading || isTokenLoading || loading}
              >
                {isLoading || isTokenLoading || loading
                  ? "Registering..."
                  : "Register"}
              </button>
            </div>
          </form>
        </>
      </div>
    </>
  );
};

export default RegisterForm;
